import axios from '../helpers/axios';
import qs from 'querystring';
import { logError } from '../services';
import {
  IResponse,
  ISageVendorContact,
  IVendorContact,
  IVendorDetail,
  IVendorDetailPost,
  IVendorEntityType,
  IVendorRes,
  IVendorType,
  ISageVendorContactPut
} from '../models';

const base_api = `/api/vendors`;

export const getVendors = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  isActive?: boolean;
  name?: string;
  subcontractorTypeIds?: string | number;
  vendorTypeIds?: number[];
  hasUsers?: boolean;
  hasPunchLists?: boolean;
  territoryId?: string | number;
  sageJobId?: number;
}): Promise<IVendorRes> => {
  try {
    const { data } = await axios.get(base_api, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getVendors failed');
    return Promise.reject(error);
  }
};

export const getVendor = async (id: number | string): Promise<IVendorDetail | any> => {
  try {
    const { data } = await axios.get(`${base_api}/${id}`, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError(error, 'getVendor failed');
    return Promise.reject(error.response.data);
  }
};

export const createVendor = async (vendor: IVendorDetailPost): Promise<any> => {
  try {
    const { data } = await axios.post(`${base_api}`, vendor, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: vendor }, 'createVendor failed');
    return Promise.reject(error.response.data);
  }
};

export const updateVendor = async (id: number, vendor: IVendorDetailPost): Promise<any> => {
  try {
    const { data } = await axios.put(`${base_api}/${id}`, vendor, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: { id, vendor } }, 'updateVendor failed');
    return Promise.reject(error.response.data);
  }
};

export const getVendorTypes = async (filters?: {
  hasSageId?: boolean;
}): Promise<IResponse<IVendorType[]>> => {
  try {
    const { data } = await axios.get(`${base_api}/types`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getVendorTypes failed');
    return Promise.reject(error);
  }
};

export const getVendorEntityTypes = async (): Promise<IVendorEntityType[]> => {
  try {
    const { data } = await axios.get(`${base_api}/entity-types`, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError(error, 'getVendorEntityTypes failed');
    return error.response.data;
  }
};

export const addVendorContact = async (
  vendorPartnerId: string | number,
  post: ISageVendorContactPut
): Promise<any> => {
  try {
    const { data } = await axios.post(`${base_api}/${vendorPartnerId}/contacts`, post, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: { vendorPartnerId, post } }, 'addVendorContact failed');
    return error.response.data;
  }
};

export const updateVendorContact = async (
  id: number | string,
  put: ISageVendorContactPut
): Promise<any> => {
  try {
    const { data } = await axios.put(`${base_api}/contacts/${id}`, put, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: { id, put } }, 'updateVendorContact failed');
    return error.response.data;
  }
};

export const deleteVendorContact = async (id: number | string): Promise<any> => {
  try {
    const { data } = await axios.delete(`${base_api}/contacts/${id}`, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError(error, 'deleteVendorContact failed');
    return error.response.data;
  }
};

export const uploadVendorCertificate = async (
  id,
  pdf: FormData,
  params: {
    documentTypeId?: number;
    warning?: boolean;
    stopPay?: boolean;
    expires: string;
  }
): Promise<any> => {
  try {
    const { data } = await axios.post(`${base_api}/${id}/certificates`, pdf, {
      headers: { 'Content-Type': 'application/json' },
      params
    });
    return data;
  } catch (error) {
    logError({ error, payload: { id, pdf, params } }, 'uploadVendorCertificate failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getAdminVendorContacts = async (
  id,
  filters?: { isActive?: boolean; role?: string }
): Promise<IVendorContact[]> => {
  try {
    const { data } = await axios.get(`${base_api}/${id}/contacts`, {
      headers: { 'Content-Type': 'application/json' },
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getAdminVendorContacts failed');
    return Promise.reject(error);
  }
};

export const getVendorContacts = async (
  id,
  filters?: { isActive?: boolean; role?: string }
): Promise<IVendorContact[]> => {
  try {
    const { data } = await axios.get(`${base_api}/common/${id}/contacts`, {
      headers: { 'Content-Type': 'application/json' },
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getVendorContacts failed');
    return Promise.reject(error);
  }
};

export const getSageVendorContacts = async (id): Promise<IResponse<ISageVendorContact[]>> => {
  try {
    const { data } = await axios.get(`${base_api}/${id}/sage-contacts`, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError(error, 'getSageVendorContacts failed');
    return Promise.reject(error);
  }
};

export const getVendorCertificatesExpiring = async (ids: number[]): Promise<string[]> => {
  try {
    const { data } = await axios.get(`${base_api}/certificates/expiring`, {
      params: {
        vendorIds: ids
      },
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getVendorCertificatesExpiring failed');
    return Promise.reject(error);
  }
};

export const addUserToPartner = async (
  id: number,
  personId: number,
  post: { roles: string[] }
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/partners/${id}/affiliates/${personId}`, post, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError({ error, payload: { id, personId, post } }, 'addUserToPartner failed');
    return error.response.data;
  }
};
